import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { IconService } from '@isophi/core-ui';
import { startWith, switchMap } from 'rxjs/operators';

import {
  buildSocialDisadvantageCode,
  calculateSocialDisadvantageCategory,
  parseSocialDisadvantageCode,
  SOCIAL_DISADVANTAGE_FACTORS,
  SOCIAL_DISADVANTAGE_SCORES,
  SocialDisadvantageScores,
} from './social-disadvantage-calculator.util';

const DEFAULT_SCORES: SocialDisadvantageScores = Object.fromEntries(SOCIAL_DISADVANTAGE_FACTORS.map((factor) => [factor.key, 0]));

@Component({
  selector: 'app-social-disadvantage-calculator',
  templateUrl: './social-disadvantage-calculator.component.html',
  styleUrls: ['./social-disadvantage-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SocialDisadvantageCalculatorComponent {
  readonly childForm = input.required<FormGroup>();

  readonly codeControlName = input('socialDisadvantageCode');

  readonly iconService = inject(IconService);

  readonly factors = SOCIAL_DISADVANTAGE_FACTORS;

  readonly scores = SOCIAL_DISADVANTAGE_SCORES;

  private readonly codeControl = computed(() => this.childForm().get(this.codeControlName()) as FormControl<string>);

  private readonly code = toSignal(
    toObservable(this.codeControl).pipe(switchMap((control) => control.valueChanges.pipe(startWith(control.value))))
  );

  readonly hasValidCode = computed(() => parseSocialDisadvantageCode(this.code()) !== null);

  readonly factorScores = computed(() => parseSocialDisadvantageCode(this.code()) ?? DEFAULT_SCORES);

  readonly category = computed(() => calculateSocialDisadvantageCategory(this.factorScores()));

  select(factorKey: string, score: number): void {
    const nextScores = { ...this.factorScores(), [factorKey]: score };
    this.codeControl().setValue(buildSocialDisadvantageCode(nextScores));
  }
}
